/**
 * Where the robots' charging docks are, for the maps to mark.
 *
 * THE DATA PATH. Nothing on the wire says "this place is a charger": the
 * place registry (`usePlaces`) carries name, level, x, y, the owning fleets
 * and three capability flags, and `is_charger` is deliberately not one of
 * them (v1 contract `PlaceRegistry`). What DOES name the chargers is the
 * task catalog (`useTaskCatalog`): its `charge` entry's `places` are the
 * site's declared `charger_places` (`sites/<id>.yml`), intersected by the
 * worker with the live navigation graph -- so a declared name the graph does
 * not have never reaches here. The position comes from the registry row of
 * the same name. A dock is therefore drawn only where BOTH say so.
 *
 * NEVER A GUESS. No catalog yet, no `charge` category, a charger the
 * registry does not list, a row whose floor the worker could not resolve
 * (`level: ''`, a registry published before the building map arrived) or
 * whose coordinates are not numbers: each draws nothing. The 2D map keeps
 * an unresolved-floor place as a waypoint dot (texture, and transient); a
 * dock is a claim about where something physical stands, so it is not
 * drawn on a floor nobody named.
 *
 * WORDS. A site audience reads "Charging dock" and nothing else -- no
 * waypoint name, no code, no metres. Ops also get the place name, in the
 * same sentence, because that is the handle they look things up by.
 */
import type { PlaceRegistry, TaskCatalogEntry } from '../../api/v1';
import type { Audience } from '../../auth/roles';
import { sameLevel } from '../../utils/levels';

/** The catalog category whose places are the site's chargers. */
export const CHARGE_CATEGORY = 'charge';

/** What every audience reads for a dock. */
export const DOCK_LABEL = 'Charging dock';

/** One charging dock, where the registry puts it (map frame, the frame the
 *  robots and every other place are drawn in). */
export interface DockPlace {
  /** The waypoint name. Ops wording only; never shown to a site audience. */
  readonly name: string;
  /** The map's level name, as the registry spells it. Never empty. */
  readonly level: string;
  readonly x: number;
  readonly y: number;
}

const NO_DOCKS: readonly DockPlace[] = Object.freeze([]);

/**
 * Every dock the data supports: the `charge` category's places, each at its
 * registry row. In catalog order; a name with no usable row is left out.
 */
export function dockPlaces(
  catalog: readonly TaskCatalogEntry[] | undefined,
  places: readonly PlaceRegistry[] | undefined,
): readonly DockPlace[] {
  if (!catalog || !places || places.length === 0) return NO_DOCKS;
  const names = catalog.find((e) => e.category === CHARGE_CATEGORY)?.places ?? [];
  if (names.length === 0) return NO_DOCKS;
  const out: DockPlace[] = [];
  const seen = new Set<string>();
  for (const name of names) {
    if (seen.has(name)) continue;
    seen.add(name);
    for (const p of places) {
      if (p.name !== name) continue;
      if (!p.level) continue;
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
      out.push({ name: p.name, level: p.level, x: p.x, y: p.y });
    }
  }
  return out.length === 0 ? NO_DOCKS : out;
}

/** The docks on one floor (either spelling of its name, `sameLevel`). An
 *  empty floor name holds none. */
export function docksOnLevel(
  docks: readonly DockPlace[],
  level: string | null | undefined,
): readonly DockPlace[] {
  if (!level || docks.length === 0) return NO_DOCKS;
  const here = docks.filter((d) => sameLevel(d.level, level));
  return here.length === 0 ? NO_DOCKS : here;
}

/** The words for one dock, for its reader: "Charging dock" for a site
 *  audience; ops also get the waypoint name. */
export function dockWords(dock: DockPlace, audience: Audience): string {
  return audience === 'ops' ? `${DOCK_LABEL} · ${dock.name}` : DOCK_LABEL;
}
